import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Tabs,
  Tab,
  IconButton,
  Chip,
  Avatar,
  AvatarGroup,
  LinearProgress,
  Breadcrumbs,
  Link,
  Skeleton,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  ArrowLeft,
  Plus,
  Settings,
  MoreVertical,
  Clock,
  Users,
  FolderTree,
  ListTodo,
  BarChart3,
  Calendar,
  Edit2,
  Trash2,
  ChevronRight,
  GanttChart,
  Target,
  AlertTriangle,
  FolderPlus,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { CheckCircle } from 'lucide-react';

import { useAppDispatch, useAppSelector } from '@/store';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { openModal } from '@/store/slices/uiSlice';
import { Project, ProjectStatus, Task, TaskStatus } from '@/types';
import { projectService, taskService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';
import StatusBadge from '@/shared/components/StatusBadge';
import { useFeaturePermission } from '@/shared/contexts/PermissionsContext';
import { useSnackbar } from '@/shared/contexts/SnackbarContext';
import { isAdminOrManager } from '@/shared/utils/roles';
import {
  DEFAULT_WORK_HOURS_PER_DAY,
  calculateEstimatedHoursFromDates,
} from '@/shared/utils/projectDuration';
import ProjectTasksTab from '../components/ProjectTasksTab';
import ProjectTreeView from '../components/ProjectTreeView';
import ProjectOverviewTab from '../components/ProjectOverviewTab';
import ProjectTeamTab from '../components/ProjectTeamTab';
import MilestonesManagement from '../components/MilestonesManagement';
import ProjectEditModal from '../components/ProjectEditModal';
import ProjectSettingsModal from '../components/ProjectSettingsModal';
import DeleteProjectDialog from '../components/DeleteProjectDialog';
import ProjectTasksGanttChart from '../components/ProjectTasksGanttChart';
import ProjectReports from '../components/ProjectReports';
import RiskManagement from '../components/RiskManagement';
import ConfirmationModal from '@/shared/components/modals/ConfirmationModal';
import { formatPercent } from '@/shared/utils/formatPercentage';
import TaskDetailModal from '@/features/tasks/components/TaskDetailModal';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`project-tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

// Project status colors
const statusColors: Record<ProjectStatus, string> = {
  [ProjectStatus.PLANNING]: '#64748b',
  [ProjectStatus.ACTIVE]: '#4F46E5',
  [ProjectStatus.ON_HOLD]: '#f59e0b',
  [ProjectStatus.COMPLETED]: '#16a34a',
  [ProjectStatus.CANCELLED]: '#ef4444',
};

/**
 * ProjectDetailPage
 * Comprehensive project view with tasks, hierarchy, team
 * Master project management interface
 */
const ProjectDetailPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
    const { showError, showSuccess } = useSnackbar();
  const user = useAppSelector(selectCurrentUser);
  
  // Permission checks — role fallback for managers/admins when dynamic permissions lag
  const canCreateTask = useFeaturePermission('tasks', 'task_management', 'create');
  const hasEditPermission = useFeaturePermission('projects', 'project_crud', 'edit');
  const hasDeletePermission = useFeaturePermission('projects', 'project_crud', 'delete');
  const canManageProjectsByRole = isAdminOrManager(user?.role);
  const canEditProject = canManageProjectsByRole || hasEditPermission;
  const canDeleteProject = canManageProjectsByRole || hasDeletePermission;
  
  const [activeTab, setActiveTab] = useState(0);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  
  // Modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [closeProjectConfirmOpen, setCloseProjectConfirmOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);

  // Fetch project details
  const { data: projectData, isLoading: projectLoading, isError: projectError, error: projectErrorData } = useQuery({
    queryKey: QUERY_KEYS.project(projectId!),
    queryFn: () => projectService.getProject(projectId!),
    enabled: !!projectId,
    retry: 1,
    retryDelay: 1000,
  });

  const project = projectData ?? undefined;

  // Fetch project tasks
  const { data: tasksData, isLoading: tasksLoading, isError: tasksError } = useQuery({
    queryKey: QUERY_KEYS.projectTasks(projectId!),
    queryFn: () => taskService.getTasksByProject(projectId!),
    enabled: !!projectId && !!project,
    retry: 1,
    retryDelay: 1000,
  });

  const tasks = tasksData ?? [];

  // Fetch child projects
  const { data: childProjectsData, isError: childProjectsError } = useQuery({
    queryKey: ['projects', projectId, 'children'],
    queryFn: () => projectService.getProjectChildren(projectId!),
    enabled: !!projectId && !!project,
    retry: 2,
    retryDelay: 1000,
  });

  const childProjects = childProjectsData ?? [];

  const closeProjectMutation = useMutation({
    mutationFn: () =>
      projectService.updateProject(projectId!, { status: ProjectStatus.COMPLETED }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.project(projectId!) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.projects });
      setCloseProjectConfirmOpen(false);
    },
  });

  const handleCloseProjectConfirm = () => {
    closeProjectMutation.mutate();
  };

  // Delete task mutation
  const deleteTaskMutation = useMutation({
    mutationFn: (taskId: string) => taskService.deleteTask(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.projectTasks(projectId!) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
      setSelectedTaskId(null);
    },
    onError: (error) => {
      showError(error instanceof Error ? error.message : 'Failed to delete task');
    },
  });

  const ensureDocumentsFolderMutation = useMutation({
    mutationFn: () => projectService.ensureDocumentsFolder(projectId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.project(projectId!) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.documentCategories() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.documentCategories(true) });
      handleMenuClose();
      showSuccess(`Documents folder created for ${project?.projRef || 'project'}`);
    },
    onError: (error) => {
      showError(error instanceof Error ? error.message : 'Failed to create Documents folder');
    },
  });

  const calculateStats = () => {
    const taskEstimatedTime = tasks.reduce(
      (sum: number, t: Task) => sum + (t.estimatedMinutes || 0),
      0
    );

    if (!tasks.length && !project) {
      return {
        totalTasks: 0,
        completedTasks: 0,
        progress: 0,
        totalTime: 0,
        taskEstimatedTime: 0,
        projectEstimatedTime: 0,
        estimatedTime: 0,
      };
    }

    const completedTasks = tasks.filter(
      (t: Task) => t.status === TaskStatus.COMPLETED
    ).length;
    const progressFromTasks = tasks.length
      ? Math.round((completedTasks / tasks.length) * 100)
      : 0;
    const progress =
      project?.status === ProjectStatus.COMPLETED ? 100 : progressFromTasks;
    const totalTime = tasks.reduce(
      (sum: number, t: Task) => sum + (t.actualMinutes || 0),
      0
    );

    const workHoursPerDay = user?.workHoursPerDay ?? DEFAULT_WORK_HOURS_PER_DAY;
    const dateBasedEstimateMinutes = project
      ? Math.round(
          (calculateEstimatedHoursFromDates(project.startDate, project.endDate, workHoursPerDay) ?? 0) * 60
        )
      : 0;
    const projectEstimatedTime = project?.estimatedMinutes ?? dateBasedEstimateMinutes;
    const estimatedTime = projectEstimatedTime > 0 ? projectEstimatedTime : taskEstimatedTime;

    return {
      totalTasks: tasks.length,
      completedTasks,
      progress,
      totalTime,
      taskEstimatedTime,
      projectEstimatedTime,
      estimatedTime,
    };
  };

  const stats = calculateStats();

  const handleCreateTask = () => {
    dispatch(openModal({
      type: 'taskCreate',
      data: {
        projectId,
        projectEstimatedMinutes: project?.estimatedMinutes,
        projectStartDate: project?.startDate,
        projectEndDate: project?.endDate,
        projectAllocatedMinutes: stats.taskEstimatedTime,
      },
    }));
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleEditProject = () => {
    handleMenuClose();
    setEditModalOpen(true);
  };

  const handleOpenSettings = () => {
    handleMenuClose();
    setSettingsModalOpen(true);
  };

  const handleDeleteProject = () => {
    handleMenuClose();
    setDeleteDialogOpen(true);
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  if (projectLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width="60%" height={40} />
        <Skeleton variant="text" width="40%" />
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mt: 3 }}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="rounded" height={100} />
          ))}
        </Box>
        <Skeleton variant="rounded" height={400} sx={{ mt: 3 }} />
      </Box>
    );
  }

  if (projectError || !project) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error" gutterBottom>
          {projectError ? 'Failed to load project' : 'Project not found'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {projectError 
            ? 'There was an error loading the project. Please try again or check your connection.' 
            : 'The project you are looking for does not exist or you do not have access to it.'}
        </Typography>
        {projectErrorData && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mb: 2, fontFamily: 'monospace' }}>
            Error: {String(projectErrorData)}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Project ID: {projectId}
        </Typography>
        <Button
          startIcon={<ArrowLeft size={16} />}
          onClick={() => navigate('/projects')}
          variant="contained"
        >
          Back to Projects
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          underline="hover"
          color="inherit"
          onClick={() => navigate('/')}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          Dashboard
        </Link>
        <Link
          component="button"
          underline="hover"
          color="inherit"
          onClick={() => navigate('/projects')}
        >
          Projects
        </Link>
        {project.parentId && (
          <Link
            component="button"
            underline="hover"
            color="inherit"
            onClick={() => navigate(`/projects/${project.parentId}`)}
          >
            Parent Project
          </Link>
        )}
        <Typography color="text.primary">{project.name}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <IconButton onClick={() => navigate(-1)} size="small">
            <ArrowLeft size={20} />
          </IconButton>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="h5" fontWeight={600}>
                {project.name}
              </Typography>
              <Chip
                label={project.status.replace('_', ' ')}
                size="small"
                sx={{
                  bgcolor: `${statusColors[project.status]}20`,
                  color: statusColors[project.status],
                  fontWeight: 500,
                  textTransform: 'capitalize',
                }}
              />
              {canEditProject && project.status !== ProjectStatus.COMPLETED && project.status !== ProjectStatus.CANCELLED && (
                <Chip
                  icon={<CheckCircle size={14} />}
                  label="Close Project"
                  size="small"
                  onClick={() => setCloseProjectConfirmOpen(true)}
                  sx={{
                    bgcolor: 'success.50',
                    color: 'success.main',
                    fontWeight: 500,
                    '&:hover': { bgcolor: 'success.100' },
                  }}
                />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {project.description || 'No description'}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {canCreateTask && (
            <Button
              variant="contained"
              startIcon={<Plus size={16} />}
              onClick={handleCreateTask}
            >
              Add Task
            </Button>
          )}
          <Button variant="outlined" onClick={() => navigate(`/tickets?projectId=${project.id}`)}>
            Tickets
          </Button>
          {(canEditProject || canDeleteProject) && (
            <>
              <IconButton onClick={handleMenuOpen}>
                <MoreVertical size={20} />
              </IconButton>
              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
              >
                {canEditProject && (
                  <MenuItem onClick={handleEditProject}>
                    <Edit2 size={16} style={{ marginRight: 8 }} />
                    Edit Project
                  </MenuItem>
                )}
                {canEditProject && (
                  <MenuItem onClick={handleOpenSettings}>
                    <Settings size={16} style={{ marginRight: 8 }} />
                    Settings
                  </MenuItem>
                )}
                {canEditProject && !project.driveFolderId && (
                  <MenuItem
                    onClick={() => ensureDocumentsFolderMutation.mutate()}
                    disabled={ensureDocumentsFolderMutation.isPending}
                  >
                    <FolderPlus size={16} style={{ marginRight: 8 }} />
                    {ensureDocumentsFolderMutation.isPending
                      ? 'Creating folder…'
                      : 'Create Documents folder'}
                  </MenuItem>
                )}
                {canEditProject && project.status !== ProjectStatus.COMPLETED && project.status !== ProjectStatus.CANCELLED && (
                  <MenuItem onClick={() => { handleMenuClose(); setCloseProjectConfirmOpen(true); }}>
                    <CheckCircle size={16} style={{ marginRight: 8 }} />
                    Close Project
                  </MenuItem>
                )}
                {canDeleteProject && (
                  <>
                    <Divider />
                    <MenuItem onClick={handleDeleteProject} sx={{ color: 'error.main' }}>
                      <Trash2 size={16} style={{ marginRight: 8 }} />
                      Delete Project
                    </MenuItem>
                  </>
                )}
              </Menu>
            </>
          )}
        </Box>
      </Box>

      {/* Stats Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 2,
          mb: 3,
        }}
      >
        {/* Progress Card */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <BarChart3 size={18} style={{ color: '#4F46E5' }} />
              <Typography variant="subtitle2" color="text.secondary">
                Progress
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight={600}>
              {formatPercent(stats.progress)}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={stats.progress}
              sx={{
                mt: 1,
                height: 6,
                borderRadius: 1,
                bgcolor: 'grey.100',
              }}
            />
          </CardContent>
        </Card>

        {/* Tasks Card */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <ListTodo size={18} style={{ color: '#16a34a' }} />
              <Typography variant="subtitle2" color="text.secondary">
                Tasks
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight={600}>
              {stats.completedTasks}/{stats.totalTasks}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              completed
            </Typography>
          </CardContent>
        </Card>

        {/* Time Card */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Clock size={18} style={{ color: '#f59e0b' }} />
              <Typography variant="subtitle2" color="text.secondary">
                Time Tracked
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight={600}>
              {formatDuration(stats.totalTime)}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              of {formatDuration(stats.estimatedTime)} project estimate
            </Typography>
            {stats.taskEstimatedTime > 0 && stats.projectEstimatedTime > 0 && (
              <Typography variant="caption" color="text.secondary">
                {formatDuration(stats.taskEstimatedTime)} allocated to tasks
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Deadline Card */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Calendar size={18} style={{ color: '#ef4444' }} />
              <Typography variant="subtitle2" color="text.secondary">
                Deadline
              </Typography>
            </Box>
            <Typography variant="h5" fontWeight={600}>
              {dayjs(project.endDate).format('MMM D, YYYY')}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: dayjs(project.endDate).isBefore(dayjs())
                  ? 'error.main'
                  : 'text.secondary',
              }}
            >
              {dayjs(project.endDate).fromNow()}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Tabs */}
      <Card>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 2 }}
        >
          <Tab
            label="Overview"
            icon={<BarChart3 size={16} />}
            iconPosition="start"
          />
          <Tab label="Tasks" icon={<ListTodo size={16} />} iconPosition="start" />
          <Tab
            label="Milestones"
            icon={<Target size={16} />}
            iconPosition="start"
          />
          <Tab
            label="Hierarchy"
            icon={<FolderTree size={16} />}
            iconPosition="start"
          />
          <Tab
            label="Timeline"
            icon={<GanttChart size={16} />}
            iconPosition="start"
          />
          <Tab label="Team" icon={<Users size={16} />} iconPosition="start" />
          <Tab label="Reports" icon={<BarChart3 size={16} />} iconPosition="start" />
          <Tab label="Risks" icon={<AlertTriangle size={16} />} iconPosition="start" />
        </Tabs>

        <Box sx={{ p: 2 }}>
          {/* Overview Tab */}
          <TabPanel value={activeTab} index={0}>
            <ProjectOverviewTab
              project={project}
              tasks={tasks}
              stats={stats}
            />
          </TabPanel>

          {/* Tasks Tab */}
          <TabPanel value={activeTab} index={1}>
            <ProjectTasksTab
              projectId={projectId!}
              tasks={tasks}
              isLoading={tasksLoading}
              onCreateTask={handleCreateTask}
              onTaskClick={(taskId) => setSelectedTaskId(taskId)}
              canCreateTask={canCreateTask}
            />
          </TabPanel>

          {/* Milestones Tab */}
          <TabPanel value={activeTab} index={2}>
            <MilestonesManagement
              project={project}
              tasks={tasks}
            />
          </TabPanel>

          {/* Hierarchy Tab */}
          <TabPanel value={activeTab} index={3}>
            <ProjectTreeView
              project={project}
              childProjects={childProjects}
              tasks={tasks}
              onTaskClick={(taskId) => setSelectedTaskId(taskId)}
            />
          </TabPanel>

          {/* Timeline Tab */}
          <TabPanel value={activeTab} index={4}>
            <ProjectTasksGanttChart
              project={project}
              tasks={tasks}
              milestones={project.milestones || []}
              isLoading={tasksLoading}
            />
          </TabPanel>

          {/* Team Tab */}
          <TabPanel value={activeTab} index={5}>
            <ProjectTeamTab project={project} tasks={tasks} />
          </TabPanel>

          {/* Reports Tab */}
          <TabPanel value={activeTab} index={6}>
            <ProjectReports project={project} tasks={tasks} />
          </TabPanel>

          {/* Risks Tab */}
          <TabPanel value={activeTab} index={7}>
            <RiskManagement projectId={projectId!} risks={[]} />
          </TabPanel>
        </Box>
      </Card>

      {/* Project Edit Modal */}
      <ProjectEditModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        project={project}
      />

      {/* Project Settings Modal */}
      <ProjectSettingsModal
        open={settingsModalOpen}
        onClose={() => setSettingsModalOpen(false)}
        project={project}
        onDelete={() => setDeleteDialogOpen(true)}
      />

      {/* Delete Project Dialog */}
      <DeleteProjectDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        project={project}
      />

      {/* Close Project Confirmation */}
      <ConfirmationModal
        open={closeProjectConfirmOpen}
        onClose={() => setCloseProjectConfirmOpen(false)}
        data={{
          title: 'Close project',
          message: `Mark "${project?.name}" as completed? This will close the project.`,
          confirmText: 'Close project',
          cancelText: 'Cancel',
          type: 'success',
          onConfirm: handleCloseProjectConfirm,
        }}
      />

      {/* Task Detail Modal */}
      <TaskDetailModal
        open={!!selectedTaskId}
        taskId={selectedTaskId}
        onClose={() => setSelectedTaskId(null)}
        onEdit={(task: Task) => {
          // Allow editing tasks that are not completed or cancelled
          if (task.status !== TaskStatus.COMPLETED && task.status !== TaskStatus.CANCELLED) {
            setSelectedTaskId(null);
            dispatch(openModal({ type: 'taskEdit', data: task }));
          }
        }}
        onDelete={(taskId: string) => {
          deleteTaskMutation.mutate(taskId);
        }}
      />
    </Box>
  );
};

export default ProjectDetailPage;
